import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import isElectron from 'is-electron';
import { useLocalStorage } from '@vueuse/core';
import { ft8Service } from '@/services/Ft8Service';
import { wsjtXLogger } from '@/utils/loggers';

interface Ft8Config {
  enabled: boolean;
  udpAddress: string;
  udpPort: number;
  autoAccept: boolean;
}

interface Ft8State {
  isSupported: boolean;
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
  connectionError: string | null;
  lastHeartbeat: Date | null;
  lastQsoReceived: Date | null;
  qsosReceived: number;
  qsosLogged: number;
}

export const useFt8Store = defineStore('ft8', () => {
  // Track if listeners have been initialized and store cleanup functions
  const listenersInitialized = ref(false);
  const listenerCleanupFunctions = ref<(() => void)[]>([]);

  // Configuration - persisted to localStorage
  const enabled = useLocalStorage('pinia/ft8/enabled', false);
  const udpAddress = useLocalStorage('pinia/ft8/udpAddress', '127.0.0.1');
  const udpPort = useLocalStorage('pinia/ft8/udpPort', 2237);
  const autoAccept = useLocalStorage('pinia/ft8/autoAccept', true);

  // Runtime state
  const isSupported = ref(isElectron());
  const connectionStatus = ref<Ft8State['connectionStatus']>('disconnected');
  const connectionError = ref<string | null>(null);
  const lastHeartbeat = ref<Date | null>(null);
  const lastQsoReceived = ref<Date | null>(null);

  // Statistics
  const qsosReceived = ref(0);
  const qsosLogged = ref(0);

  // Computed
  const isConnected = computed(() => connectionStatus.value === 'connected');

  const config = computed<Ft8Config>(() => ({
    enabled: enabled.value,
    udpAddress: udpAddress.value,
    udpPort: udpPort.value,
    autoAccept: autoAccept.value
  }));

  // Actions
  async function startServer() {
    if (!isSupported.value) {
      connectionError.value = 'FT8 integration is only available in the desktop version';
      return false;
    }

    connectionStatus.value = 'connecting';
    connectionError.value = null;

    try {
      const result = await (window as any).api.ft8.startServer({
        address: udpAddress.value,
        port: udpPort.value
      });

      if (result.success) {
        // Server is now running, but we don't know WSJT-X status yet
        // connectionStatus will be updated when we receive messages from WSJT-X
        initIpcIfNeeded();
        return true;
      } else {
        connectionStatus.value = 'error';
        connectionError.value = result.error || 'Failed to start UDP server';
        if (result.error?.includes('EADDRINUSE')) {
          wsjtXLogger(`Port ${udpPort.value} is already in use`);
        }
        return false;
      }
    } catch (error) {
      connectionStatus.value = 'error';
      connectionError.value = error instanceof Error ? error.message : 'Unknown error';
      return false;
    }
  }

  async function stopServer() {
    if (!isSupported.value) return;

    try {
      await (window as any).api.ft8.stopServer();
      connectionStatus.value = 'disconnected';
      connectionError.value = null;
      // Clean up listeners when server stops
      cleanupListeners();
    } catch (error) {
      console.error('Failed to stop FT8 server:', error);
    }
  }

  async function testConnection() {
    if (!isSupported.value) return;

    try {
      const result = await (window as any).api.ft8.testConnection();
      if (result.success) {
        connectionStatus.value = 'connected';
        connectionError.value = null;
      } else {
        connectionStatus.value = 'disconnected';
        connectionError.value = result.error || 'Connection test failed';
      }
      return result.success;
    } catch (error) {
      connectionStatus.value = 'error';
      connectionError.value = error instanceof Error ? error.message : 'Test failed';
      return false;
    }
  }

  function updateHeartbeat() {
    lastHeartbeat.value = new Date();
  }

  async function handleQsoReceived(qsoData: any) {
    // LOGGED_ADIF messages only carry supplementary park/summit data; they do
    // not represent a newly logged QSO, so they bypass the QSO counters.
    if (qsoData?.type === 'loggedAdif') {
      await ft8Service.processReceivedAdif(qsoData);
      return;
    }

    qsosReceived.value++;
    lastQsoReceived.value = new Date();

    // Process the QSO using the FT8 service
    const success = await ft8Service.processReceivedQso(qsoData);
    if (success) {
      qsosLogged.value++;
    }
  }

  function resetStatistics() {
    qsosReceived.value = 0;
    qsosLogged.value = 0;
    lastHeartbeat.value = null;
    lastQsoReceived.value = null;
  }

  async function toggleIntegration() {
    if (!enabled.value) {
      wsjtXLogger('Starting FT8 UDP server');
      const success = await startServer();
      if (success) {
        enabled.value = true;
        wsjtXLogger('FT8 UDP server started successfully');
      } else {
        wsjtXLogger('Failed to start FT8 UDP server');
      }
    } else {
      wsjtXLogger('Stopping FT8 UDP server');
      await stopServer();
      enabled.value = false;
      connectionStatus.value = 'disconnected';
      connectionError.value = null;
    }
  }

  // Listener registration (idempotent)
  function initIpcListeners() {
    if (!isSupported.value || !(window as any).api?.ft8 || listenersInitialized.value) return;

    listenersInitialized.value = true;
    const api = (window as any).api;

    // Capture cleanup functions from each listener
    listenerCleanupFunctions.value.push(
      api.ft8.onHeartbeat(() => {
        updateHeartbeat();
      }),
      api.ft8.onQsoReceived((data: any) => {
        handleQsoReceived(data);
      }),
      api.ft8.onConnectionStatus((status: Ft8State['connectionStatus']) => {
        connectionStatus.value = status;
      }),
      api.ft8.onError((error: string) => {
        wsjtXLogger('Error', error);
        connectionError.value = error;
        connectionStatus.value = 'error';
      })
    );
  }

  // Guarded, idempotent initializer. Ensures listeners are created only once, on first-use.
  function initIpcIfNeeded() {
    if (!listenersInitialized.value) {
      initIpcListeners();
    }
  }

  // Listeners will be initialized on first relevant use (e.g., start/enable/auto-start)

  // Move auto-start logic into an async initializer for lifecycle control
  async function initializeOnStartup() {
    if (isSupported.value && enabled.value) {
      wsjtXLogger('Attempting to re-start FT8 UDP server');
      const success = await startServer();
      if (success) {
        wsjtXLogger('FT8 UDP server re-started successfully');
      } else {
        wsjtXLogger('Failed to re-start FT8 UDP server - disabling integration');
        enabled.value = false;
      }
    }
  }

  // Cleanup function to remove all listeners
  function cleanupListeners() {
    if (listenerCleanupFunctions.value.length > 0) {
      wsjtXLogger('Cleaning up FT8 IPC listeners');
      listenerCleanupFunctions.value.forEach(cleanup => cleanup());
      listenerCleanupFunctions.value.length = 0; // Clear the array
      listenersInitialized.value = false;
    }
  }

  return {
    // Configuration
    enabled,
    udpAddress,
    udpPort,
    autoAccept,

    // State
    isSupported,
    connectionStatus,
    connectionError,
    lastHeartbeat,
    lastQsoReceived,
    qsosReceived,
    qsosLogged,

    // Computed
    isConnected,
    config,

    // Actions
    startServer,
    stopServer,
    testConnection,
    toggleIntegration,
    resetStatistics,
    updateHeartbeat,
    handleQsoReceived,
    cleanupListeners,

    // Testing/diagnostics: allow manual initialization when needed
    initIpcForTests: initIpcIfNeeded,

    // Lifecycle-controlled startup
    initializeOnStartup
  };
});